import { Link } from "react-router-dom";

export type EmptyStateVariant =
  | "no-locations"
  | "auth"
  | "forbidden"
  | "not-found"
  | "kernel-down"
  | "offline";

export type EmptyStateProps = {
  variant: EmptyStateVariant;
  onRetry?: () => void;
};

type VariantConfig = {
  icon: string;
  headline: string;
  body: string;
  action?:
    | { label: string; kind: "retry" }
    | { label: string; kind: "link"; to: string };
};

const VARIANTS: Record<EmptyStateVariant, VariantConfig> = {
  "no-locations": {
    icon: "\u{1F5FA}",
    headline: "No locations forged yet",
    body: "Use chat to start building the world. Forged locations will appear here.",
  },
  auth: {
    icon: "\u{1F510}",
    headline: "Sign in required",
    body: "This view needs an authenticated session.",
    action: { label: "Go to login", kind: "link", to: "/login" },
  },
  forbidden: {
    icon: "\u{1F6AB}",
    headline: "Not a campaign member",
    body: "The map is visible to members of this campaign.",
  },
  "not-found": {
    icon: "?",
    headline: "Campaign not found",
    body: "We couldn't find that campaign.",
    action: { label: "Back to Launch", kind: "link", to: "/launch" },
  },
  "kernel-down": {
    icon: "!",
    headline: "Kernel unreachable",
    body: "The game kernel didn't respond. Try again in a moment.",
    action: { label: "Retry", kind: "retry" },
  },
  offline: {
    icon: "\u{1F4F6}",
    headline: "Offline",
    body: "You appear to be offline. Reconnect to refresh.",
    action: { label: "Retry", kind: "retry" },
  },
};

export const EmptyState = ({ variant, onRetry }: EmptyStateProps) => {
  const config = VARIANTS[variant];
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-6 text-center">
      <div className="mb-4 text-5xl">{config.icon}</div>
      <h2 className="mb-2 font-semibold text-lg text-zinc-100">
        {config.headline}
      </h2>
      <p className="mb-6 max-w-sm text-sm text-zinc-400">{config.body}</p>
      {config.action?.kind === "retry" && (
        <button
          className="rounded bg-zinc-800 px-4 py-2 text-sm text-zinc-200 hover:bg-zinc-700"
          onClick={onRetry}
          type="button"
        >
          {config.action.label}
        </button>
      )}
      {config.action?.kind === "link" && (
        <Link
          className="rounded bg-zinc-800 px-4 py-2 text-sm text-zinc-200 hover:bg-zinc-700"
          to={config.action.to}
        >
          {config.action.label}
        </Link>
      )}
    </div>
  );
};
